import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useResearch } from '../hooks/useResearch';
import ProgressIndicator from '../components/ProgressIndicator';
import ResearchResults from '../components/ResearchResults';
import { cn } from '../utils/cn';

const RESEARCH_PHASES = [
  { key: 'checking_cache', label: 'Checking Cache' },
  { key: 'generating_definition', label: 'Analyzing Market' },
  { key: 'generating_search_terms', label: 'Creating Search Terms' },
  { key: 'finding_companies', label: 'Finding Companies' },
  { key: 'finding_products', label: 'Discovering Products' },
  { key: 'finding_capabilities', label: 'Identifying Capabilities' },
  { key: 'analyzing_features', label: 'Analyzing Features' },
];

export default function ResearchPage() {
  const navigate = useNavigate();
  const { isAuthenticated, canMakeRequest, requestsRemaining } = useAuth();
  const { research, progress, error, startResearch, reset, isLoading } = useResearch();
  const [prompt, setPrompt] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/', prompt } });
      return;
    }

    if (!canMakeRequest) {
      navigate('/pricing');
      return;
    }

    await startResearch(prompt.trim());
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-display font-display text-primary mb-4">
            AskAIQ
          </h1>
          <p className="text-subtitle text-muted-foreground max-w-2xl mx-auto">
            Rapid in-depth market research with one prompt in 5 minutes
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto mb-12">
          <div className="relative">
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Enter a market name or definition (e.g., 'AI-powered CRM tools' or 'email marketing platforms')"
              className={cn(
                "w-full px-6 py-4 pr-36 text-lg rounded-xl border-2 border-border",
                "bg-card focus:border-secondary focus:ring-2 focus:ring-secondary/20",
                "placeholder:text-muted-foreground transition-all"
              )}
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || !prompt.trim()}
              className={cn(
                "absolute right-2 top-1/2 -translate-y-1/2",
                "px-6 py-2.5 rounded-lg font-medium",
                "bg-secondary text-secondary-foreground",
                "hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed",
                "transition-all flex items-center gap-2"
              )}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Researching
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  Research
                </>
              )}
            </button>
          </div>

          {isAuthenticated && (
            <p className="text-sm text-muted-foreground mt-2 text-center">
              {requestsRemaining} research{requestsRemaining !== 1 ? 'es' : ''} remaining
            </p>
          )}
        </form>

        {/* Progress Indicator */}
        <AnimatePresence mode="wait">
          {isLoading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-xl mx-auto mb-12"
            >
              <ProgressIndicator phases={RESEARCH_PHASES} progress={progress} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error */}
        {error && (
          <div className="max-w-xl mx-auto mb-8 p-4 rounded-lg bg-error/10 border border-error text-error">
            <p className="font-medium">Error</p>
            <p className="text-sm">{error}</p>
            <button
              onClick={reset}
              className="mt-2 text-sm underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Results */}
        {research && !isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ResearchResults research={research} />
          </motion.div>
        )}
      </div>
    </div>
  );
}
